import { useEffect, useState, useCallback } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { EVIDENCE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { TraceModal } from "@/components/TraceModal";
import { fmtDateTime, prettyStatus } from "@/lib/format";
import {
  Plus, Sparkles, FileText, Download, FileJson, AlertTriangle, Search,
  CheckCircle2, Send, Lock, Bot, Loader2, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function EvidenceWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const contractId = ws.contractId;
  const canManage = hasRole("tenant_admin", "contract_manager");
  const canApprove = hasRole("tenant_admin", "client_rep");
  const [packs, setPacks] = useState([]);
  const [active, setActive] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [period, setPeriod] = useState("");
  const [narrative, setNarrative] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [trace, setTrace] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    if (!contractId) return;
    const r = await api.get(`/evidence-packs?contract_id=${contractId}`);
    setPacks(r.data);
    if (r.data[0] && !active) openPack(r.data[0].id);
  }, [contractId]);
  useEffect(() => { load(); }, [load]);

  const openPack = async (id) => {
    const r = await api.get(`/evidence-packs/${id}`);
    setActive(r.data);
    setNarrative(r.data.narrative || "");
  };

  const generate = async () => {
    try {
      const { data } = await api.post("/evidence-packs", { contract_id: contractId, period });
      toast.success("Pack generated"); setGenOpen(false); setPeriod("");
      await load(); openPack(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const draftAi = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/evidence-packs/${active.id}/draft-narrative`);
      setActive(data); setNarrative(data.narrative || "");
      toast.success("AI draft ready — review before accepting");
    } catch (e) { toast.error(apiErr(e, "AI drafting failed")); }
    finally { setDrafting(false); }
  };

  const saveNarrative = async (accept) => {
    try {
      const { data } = await api.patch(`/evidence-packs/${active.id}`, { narrative, narrative_source: "human" });
      setActive(data);
      toast.success(accept ? "Narrative accepted" : "Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/evidence-packs/${active.id}`, { status });
      setActive(data); load();
      toast.success(`Pack ${prettyStatus(status)}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async () => {
    try {
      const { data } = await api.post(`/evidence-packs/${active.id}/approve`);
      setActive(data); load();
      toast.success("Client approval recorded");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportPdf = () => downloadFile(`/evidence-packs/${active.id}/export.pdf`, `evidence-pack-${active.period}.pdf`).catch(() => toast.error("Export failed"));
  const exportJson = () => downloadFile(`/evidence-packs/${active.id}/export.json`, `evidence-pack-${active.period}.json`).catch(() => toast.error("Export failed"));

  if (!contractId) return <p className="text-sm text-muted-foreground">Select a contract to assemble its evidence pack.</p>;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-testid={EVIDENCE.root}>
      {/* pack list */}
      <Card className="border-sage-light p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-forest-dark">Monthly packs</h3>
          {canManage && <Button size="sm" variant="outline" onClick={() => setGenOpen(true)} data-testid={EVIDENCE.generateBtn}><Plus className="mr-1 h-3.5 w-3.5" /> Generate</Button>}
        </div>
        <div className="space-y-2">
          {packs.length === 0 && <p className="text-sm text-muted-foreground">No packs yet.</p>}
          {packs.map((p) => (
            <button key={p.id} onClick={() => openPack(p.id)} data-testid={EVIDENCE.packRow(p.id)}
              className={`w-full rounded-lg border p-3 text-left transition-colors ${active?.id === p.id ? "border-forest-mid bg-sage-surface" : "hover:bg-sage-surface/60"}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-forest-dark">{p.period}</span>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <Progress value={p.completeness} className="h-1.5" />
                <span className="text-[10px] text-muted-foreground">{p.completeness}%</span>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* active pack */}
      <div className="space-y-4 lg:col-span-2">
        {!active ? (
          <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">Select or generate a pack.</Card>
        ) : (
          <>
            <Card className="border-sage-light p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl font-bold text-forest-dark">Evidence Pack · {active.period}</h2>
                  <p className="text-sm text-muted-foreground">{active.contract_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={active.status} />
                  {active.client_approval && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700"><UserCheck className="h-3.5 w-3.5" /> Client signed</span>}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Progress value={active.completeness} className="h-2" />
                <span className="text-xs font-semibold text-forest-mid">{active.completeness}% complete</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={exportPdf} data-testid={EVIDENCE.exportPdfBtn}><Download className="mr-1.5 h-4 w-4" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={exportJson} data-testid={EVIDENCE.exportJsonBtn}><FileJson className="mr-1.5 h-4 w-4" /> Data export</Button>
                {canManage && active.status === "draft" && <Button size="sm" onClick={() => setStatus("review")} className="bg-forest-mid hover:bg-forest-dark" data-testid={EVIDENCE.submitReviewBtn}><Send className="mr-1.5 h-4 w-4" /> Submit for review</Button>}
                {canApprove && (active.status === "review" || active.status === "approved") && !active.client_approval && <Button size="sm" onClick={approve} className="bg-sage-accent hover:bg-sage-accent/90" data-testid={EVIDENCE.approveBtn}><CheckCircle2 className="mr-1.5 h-4 w-4" /> Client approve</Button>}
                {canManage && active.status === "approved" && <Button size="sm" onClick={() => setStatus("released")} className="bg-forest-mid hover:bg-forest-dark" data-testid={EVIDENCE.releaseBtn}><Lock className="mr-1.5 h-4 w-4" /> Release pack</Button>}
              </div>
            </Card>

            {/* metrics */}
            <Card className="border-sage-light p-5">
              <h3 className="mb-3 font-heading text-base font-semibold text-forest-dark">Key metrics <span className="text-xs font-normal text-muted-foreground">— click to trace</span></h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {active.items.map((it) => (
                  <button key={it.id} onClick={() => setTrace({ open: true, item: it })} data-testid={EVIDENCE.traceBtn(it.id)}
                    className="group rounded-xl border border-sage-light bg-white p-3 text-left transition-all hover:border-forest-mid hover:shadow-sm">
                    <p className="font-heading text-2xl font-bold text-forest-dark">{it.value ?? "—"}<span className="text-xs font-normal text-muted-foreground"> {it.unit}</span></p>
                    <p className="text-xs text-muted-foreground">{it.label}</p>
                    <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-forest-mid opacity-0 transition-opacity group-hover:opacity-100"><Search className="h-3 w-3" /> trace</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* gaps */}
            {active.gaps?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50 p-5">
                <h3 className="mb-2 flex items-center gap-2 font-heading text-base font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Flagged gaps</h3>
                <ul className="space-y-1.5">
                  {active.gaps.map((g) => (
                    <li key={g.id} className="flex items-start gap-2 text-sm text-forest-dark">
                      <StatusBadge status={g.severity} kind="severity" /> <span>{g.message}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* AI narrative */}
            <Card className="border-sage-light p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><FileText className="h-4 w-4 text-forest-mid" /> Summary narrative</h3>
                {canManage && active.status !== "released" && (
                  <Button size="sm" variant="outline" onClick={draftAi} disabled={drafting} data-testid={EVIDENCE.draftAiBtn}>
                    {drafting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-forest-mid" />} Draft with AI
                  </Button>
                )}
              </div>

              {active.ai_generated && !active.narrative_accepted && (
                <div className="mb-3 flex items-start gap-2 rounded-r-lg border-l-4 border-amber-500 bg-amber-50/90 p-3">
                  <Bot className="mt-0.5 h-4 w-4 text-amber-600" />
                  <p className="text-xs text-amber-900">
                    <b>AI-generated draft.</b> Requires human review and acceptance before it becomes part of the released pack.
                  </p>
                </div>
              )}

              {active.status === "released" ? (
                <div className="whitespace-pre-wrap rounded-lg bg-sage-surface p-4 text-sm text-forest-dark">{active.narrative || "No narrative."}</div>
              ) : (
                <>
                  <Textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={8}
                    placeholder="Draft the client-facing summary, or generate it with AI and edit here…" data-testid={EVIDENCE.narrativeInput} />
                  {canManage && (
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => saveNarrative(false)}>Save</Button>
                      <Button size="sm" onClick={() => saveNarrative(true)} className="bg-forest-mid hover:bg-forest-dark" data-testid={EVIDENCE.acceptNarrativeBtn}><CheckCircle2 className="mr-1.5 h-4 w-4" /> Accept narrative</Button>
                    </div>
                  )}
                </>
              )}
              {active.narrative_accepted && <p className="mt-2 flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Human-reviewed and accepted</p>}
            </Card>

            {active.client_approval && (
              <Card className="border-sage-light bg-sage-surface p-4 text-sm">
                <span className="font-semibold text-forest-dark">Client sign-off:</span> {active.client_approval.approved_by} · {fmtDateTime(active.client_approval.approved_at)}
              </Card>
            )}
          </>
        )}
      </div>

      {/* generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Generate monthly pack</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reporting period (YYYY-MM)</Label>
            <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-06" data-testid={EVIDENCE.field("period")} />
            <p className="text-xs text-muted-foreground">Assembles work orders, photos, observations and metrics for this contract and flags any gaps.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button><Button onClick={generate} disabled={!period} className="bg-forest-mid hover:bg-forest-dark">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {active && <TraceModal packId={active.id} item={trace.item} open={trace.open} onClose={() => setTrace({ open: false, item: null })} />}
    </div>
  );
}
